import React, { useCallback } from 'react'

import type { BinaryFilter } from '@cubejs-client/core'
import { formatExportFileName, useCubeReportPageFiltersContext } from 'cube'
import {
  CONVERSATION_FACTS_DIMENSIONS,
  EXPORT_MESSAGE_BY_STATUS,
} from 'cube/constants'
import { RoutingSankeyDrilldownBase } from 'cube/queries'
import { RoutedChatTeammatesBaseExportQuery } from 'cube/queries/RoutedChatTeammateCSVExport.query'
import { pluralize } from 'humanize-plus'
import { isNil } from 'ramda'

import {
  Button,
  Flex,
  Header,
  Paper,
  Table,
  TableBasePaginationPageSizes,
  TableColumn,
  Text,
  UserAvatar,
  useToast,
} from '@driftt/dds'
import {
  CubeQueryBuilder,
  CubeQueryStringAdapter,
  TypeFormats,
  useCubeExport,
} from '@driftt/dds.reports'
import { tracking } from '@driftt/traffic-control'

import { trackDrillDownClick } from 'components/conversation/hooks/useTrackDrilldownClickEvent'
import { StyledHeader } from 'components/conversation/TableWrapper'
import useTimePeriod from 'hooks/useTimePeriod'
import { CONVO_DETAIL_REPORT_PATH } from 'layouts/ConversationsDetailReport/ConversationsDetailReport.constants'
import { getHumanizedDuration } from 'utils/format'
import { isNilOrEmpty } from 'utils/ramda'

import { TeammatesTableDrilldownLink } from './DrilldownLink'
import {
  DRILLDOWN_KEY,
  DrilldownUser,
  TeammatesColumnToDrilldownFilterMap,
} from './teammates.constants'
import { TeammatesTableRow } from './teammates.types'
import useRoutedChatTeammates from './useRoutedChatTeammates.hook'

const DEFAULT_PAGE_SIZE = TableBasePaginationPageSizes[3]

const toTwoDecimalPlaces = (stringNumber: string | null) => {
  if (isNil(stringNumber) || Number(stringNumber) === 0) {
    return '--'
  }

  return Number(stringNumber).toFixed(1)
}

const TeammatesTabContent = () => {
  const { tableData, isLoadingTableData } = useRoutedChatTeammates()
  const timePeriod = useTimePeriod()
  const triggerToast = useToast()

  const { pageFilters } = useCubeReportPageFiltersContext()

  const onDrilldownLinkClicked = ({
    userId,
    rowKey,
    drilldownUser = DrilldownUser.ROUTED_IN_USER,
  }) => {
    trackDrillDownClick(
      `RoutingTeammatesDrilldownTable-${rowKey}`,
      timePeriod.startDate,
      timePeriod.endDate
    )

    const { filters } = TeammatesColumnToDrilldownFilterMap[rowKey] || {}

    const filtersKey = filters.map((filter) => filter.member).join('-')

    const filtersMap = filters.map((filter) => {
      return {
        member: filter.member,
        operator: filter.operator,
        values: filter.values,
      } as BinaryFilter
    })

    const drilldownUserFilter: BinaryFilter = {
      member: drilldownUser,
      operator: 'equals',
      values: [userId],
    }

    const drilldownQuery = new CubeQueryBuilder({
      query: RoutingSankeyDrilldownBase,
    })
      .withName(`${DRILLDOWN_KEY}-${filtersKey}`)
      .withQueryName(`${DRILLDOWN_KEY}-${filtersKey}`)
      .withinDateRange(
        CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
        timePeriod.startDate.toDate(),
        timePeriod.endDate.toDate()
      )
      .withFilters([...filtersMap, ...pageFilters, drilldownUserFilter])
      .build()

    const drillDownQueryString =
      CubeQueryStringAdapter.toQueryString(drilldownQuery)

    const drillDownURL = `${CONVO_DETAIL_REPORT_PATH}?${drillDownQueryString}`
    window.open(drillDownURL, '_blank')
  }

  const exportCubeDataQuery = new CubeQueryBuilder({
    query: RoutedChatTeammatesBaseExportQuery,
  })
    .withName('RoutedChatTeammatesExport')
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      timePeriod.startDate.toDate(),
      timePeriod.endDate.toDate()
    )
    .withFilters([...pageFilters])
    .build()

  const { isInitialLoading: isDownloadingExport, refetch: exportCubeData } =
    useCubeExport({
      query: exportCubeDataQuery,
      enabled: false,
      exportFileName: formatExportFileName(
        'Teammates',
        timePeriod.startDate.toDate(),
        timePeriod.endDate.toDate()
      ),
      onExportSuccess() {
        triggerToast({
          message: EXPORT_MESSAGE_BY_STATUS.SUCCESS,
          variant: 'success',
        })
      },
      onExportError() {
        triggerToast({
          message: EXPORT_MESSAGE_BY_STATUS.ERROR,
          variant: 'error',
        })
      },
    })

  const onExportButtonClick = useCallback(() => {
    tracking.trackEvent('CUBE_DRILL_DOWN_EXPORT_CLICK', {
      metricType: 'RoutedChatsTeammatesExport',
      startDate: timePeriod.startDate.toDate(),
      endDate: timePeriod.endDate.toDate(),
      pageName: window.location.pathname,
    })
    exportCubeData()
  }, [exportCubeData, timePeriod.startDate, timePeriod.endDate])

  const tableColumns: TableColumn<TeammatesTableRow>[] = [
    {
      header: 'Name',
      value: 'displayName',
      cell: ({ displayName, avatarUrl }) => (
        <Flex alignItems="center">
          <UserAvatar size={'small'} name={displayName} imageUrl={avatarUrl} />
          <Text isPrivate>{displayName} </Text>
        </Flex>
      ),
      disableSort: true,
      size: 170,
    },
    {
      header: 'Routed chats',
      value: 'count',
      cell: ({ count, userId }) => (
        <TeammatesTableDrilldownLink
          column={'routed-chats'}
          value={count}
          onClick={() => onDrilldownLinkClicked({ userId, rowKey: 'count' })}
        />
      ),
      disableSort: true,
      size: 94,
    },
    {
      header: 'Missed chats',
      value: 'countTeammateRoutedInToMissed',
      cell: ({ countTeammateRoutedInToMissed, count, userId }) => {
        const percent = (countTeammateRoutedInToMissed / count) * 100
        return (
          <Flex direction="column">
            <TeammatesTableDrilldownLink
              column={'missed-chats'}
              value={countTeammateRoutedInToMissed}
              onClick={() =>
                onDrilldownLinkClicked({
                  userId,
                  rowKey: 'countTeammateRoutedInToMissed',
                })
              }
            />
            <Text>{TypeFormats.PERCENT(percent)}</Text>
          </Flex>
        )
      },
      disableSort: true,
      size: 94,
    },
    {
      header: 'Teammate responded',
      value: 'countTeammateRoutedInAndResponded',
      cell: ({ countTeammateRoutedInAndResponded, count, userId }) => {
        const percent = (countTeammateRoutedInAndResponded / count) * 100
        return (
          <Flex direction="column">
            <TeammatesTableDrilldownLink
              column={'teammate-responded'}
              value={countTeammateRoutedInAndResponded}
              onClick={() =>
                onDrilldownLinkClicked({
                  userId,
                  rowKey: 'countTeammateRoutedInAndResponded',
                  drilldownUser: DrilldownUser.USER,
                })
              }
            />
            <Text>{TypeFormats.PERCENT(percent)}</Text>
          </Flex>
        )
      },
      disableSort: true,
      size: 94,
    },
    {
      header: 'Average time to first response',
      value: 'averageTimeToFirstRoutedUserResponse',
      cell: ({ averageTimeToFirstRoutedUserResponse }) => (
        <Text>
          {getHumanizedDuration(
            averageTimeToFirstRoutedUserResponse,
            'seconds'
          )}
        </Text>
      ),
      disableSort: true,
      size: 136,
    },

    {
      header: 'Unresponsive fallback fired',
      value: 'countConversationsWithUnresponsiveFallbackEvents',
      cell: ({ countConversationsWithUnresponsiveFallbackEvents, userId }) => (
        <TeammatesTableDrilldownLink
          column={'unresponsive-fallbacks'}
          onClick={() =>
            onDrilldownLinkClicked({
              userId,
              rowKey: 'countConversationsWithUnresponsiveFallbackEvents',
            })
          }
          value={countConversationsWithUnresponsiveFallbackEvents}
        />
      ),
      disableSort: true,
      size: 94,
    },
    {
      header: 'Median chat length',
      value: 'medianDurationBetweenFirstInteractionAndFirstCloseSeconds',
      cell: ({ medianDurationBetweenFirstInteractionAndFirstCloseSeconds }) => (
        <Text>
          {getHumanizedDuration(
            medianDurationBetweenFirstInteractionAndFirstCloseSeconds,
            'seconds'
          )}
        </Text>
      ),
      disableSort: true,
      size: 96,
    },
    {
      header: 'Meetings booked',
      value: 'countMeetingBooked',
      cell: ({ countMeetingBooked }) => <Text>{countMeetingBooked}</Text>,
      disableSort: true,
      size: 136,
    },
    {
      header: 'Average rating',
      value: 'ratingAverage',
      cell: ({ ratingAverage, ratingCount, userId }) => (
        <Flex direction="column">
          <Text>{toTwoDecimalPlaces(ratingAverage)}</Text>
          <TeammatesTableDrilldownLink
            column="average-rating"
            value={`${ratingCount ?? 0}
            ${pluralize(ratingCount, 'rating', 'ratings')}`}
            onClick={() =>
              onDrilldownLinkClicked({
                userId,
                rowKey: 'countAverageRating',
              })
            }
          />
        </Flex>
      ),
      disableSort: true,
      size: 150,
    },
  ]
  return (
    <Paper elevation={2} as={Flex} direction="column" alignItems={'stretch'}>
      <StyledHeader justifyContent="space-between" direction="column">
        <Flex fullWidth justifyContent="space-between">
          <Header variant="h3">Routed chats</Header>
          <Button
            variant="secondary"
            loading={isDownloadingExport}
            onClick={() => onExportButtonClick()}
            disabled={isNilOrEmpty(tableData)}
            rootProps={{
              id: 'routed-chats-report--teammates-export-button',
            }}
          >
            Export CSV
          </Button>
        </Flex>
        <Text variant="metadata">
          Understand how teammates are responding to routed chats. These stats
          only pertain to the first teammate routed into the chat. They don't
          include instances where the teammate was routed in after (such as when
          the conversation was re-routed).
        </Text>
      </StyledHeader>
      <Table
        columns={tableColumns}
        isLoading={isLoadingTableData}
        rows={tableData}
        disableBoxShadow={true}
        loadingRows={10}
        pagination={{
          rowTotal: tableData.length ?? DEFAULT_PAGE_SIZE,
          defaultPagination: {
            pageIndex: 0,
            pageSize: DEFAULT_PAGE_SIZE,
          },
          enableClientSidePagination: true,
        }}
      />
    </Paper>
  )
}

export default TeammatesTabContent
